import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../seo/Seo';
import { breadcrumbLd } from '../seo/jsonLd';
import Breadcrumb from './Breadcrumb';
import AfkPipeline from './AfkPipeline';
import CTASection from './CTASection';

const CRUMBS = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/#services' },
  { name: 'AFK Development' },
];

const AfkDevelopmentPage: React.FC = () => (
  <div className="bg-gray-900 text-white">
    <Seo
      title="AFK Development: away-from-keyboard development | Kult.io"
      description="Humans align on the spec and review the pull request; agents implement everything in between, inside a pipeline of checks they cannot skip. How it runs, and what it refuses to do."
      path="/afk-development"
      jsonLd={breadcrumbLd(CRUMBS)}
    />

    <div className="border-b border-gray-800 bg-gray-950">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 md:py-20">
        <h1 className="font-display text-display-md font-bold text-balance text-white sm:text-display-lg">
          AFK Development: humans at both ends, agents in the middle.
        </h1>
        <p className="mt-6 max-w-measure text-lg text-gray-300">
          AFK is away from keyboard. Your people align on what to build and review what came back;
          the implementation in between runs unattended. It is how we deliver, and it is the
          pipeline we set up inside client teams.
        </p>
      </div>
    </div>

    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 md:py-20">
      <Breadcrumb crumbs={CRUMBS} />

      <p className="mt-12 max-w-measure text-lg text-gray-300">
        On a client engagement running this pipeline,{' '}
        <strong className="font-medium text-white">development throughput went up 70%</strong>, and
        85% of that project&rsquo;s code was agent-generated. Every pull request still met a human.{' '}
        <Link
          to="/success-stories/afk-development"
          className="rounded text-accent-400 transition-colors duration-150 hover:text-accent-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
        >
          Read the case study
        </Link>
        .
      </p>

      <section className="mt-16 md:mt-20">
        <h2 className="font-display text-display-sm font-bold text-white sm:text-display-md">
          The pipeline
        </h2>
        <p className="mt-4 max-w-measure text-gray-300">
          Every constraint here exists because of a failure it prevents. That is the whole design.
        </p>
        <AfkPipeline className="mt-12" />
      </section>

      <section className="mt-16 grid gap-x-10 gap-y-6 md:mt-20 lg:grid-cols-[1fr_2fr]">
        <h2 className="font-display text-display-sm font-bold text-white sm:text-display-md">
          Why ours runs in the cloud
        </h2>
        <div className="max-w-measure space-y-6 text-lg text-gray-300">
          <p>
            The usual setup runs on a developer&rsquo;s laptop, which caps you at the parallel work
            one machine can hold and kills a run when the lid closes. We run pipelines in cloud
            development environments instead, so slices run in parallel without fighting for one
            machine, and Claude, current stacks and current plugins stay the same across every
            environment.
          </p>
          <p className="text-gray-400">
            The honest constraint: run as many pipelines as your review capacity can absorb, and no
            more. The bottleneck moves to human judgment, which is exactly where it should sit.
          </p>
        </div>
      </section>
    </div>

    <CTASection placement="afk-development" />
  </div>
);

export default AfkDevelopmentPage;
